// "التقرير الأسبوعي" — the B2C sales week in figures, for sales and admins:
// orders generated, orders contacted, editions running, paid registrations
// and fill, CA generated and money collected, with the two ratios that drive
// the week — contacted → paid, and collected ÷ CA — each against the week
// before. Weeks run Sunday → Saturday; the figures' rules live in
// src/lib/weekly-report.ts and the `weeklyReport.get` endpoint.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ChevronLeft,
	ChevronRight,
	Loader2,
	Printer,
	TrendingDown,
	TrendingUp,
} from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { GhostButton } from "#/components/admin/modal";
import { editionLabel } from "#/lib/editions";
import { algiersToday } from "#/lib/lead-filters";
import { formatFigure } from "#/lib/marketing-report";
import { canViewWeeklyReport, homeRouteFor } from "#/lib/roles";
import { getSession } from "#/lib/session";
import {
	addDays,
	isoDaySchema,
	type WeekCounts,
	weekChange,
	weekLabel,
	weekRates,
	weekStart,
} from "#/lib/weekly-report";

const searchSchema = z.object({ week: isoDaySchema.optional() });

type WeeklySearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/weekly-report")({
	validateSearch: (search: Record<string, unknown>): WeeklySearch => {
		const parsed = searchSchema.safeParse(search);
		return parsed.success ? parsed.data : {};
	},
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		if (!canViewWeeklyReport(session.user.role)) {
			throw redirect({ to: homeRouteFor(session.user.role) });
		}
	},
	component: WeeklyReportPage,
});

const PRINT_CSS = `@media print {
	@page { size: A4 portrait; margin: 12mm; }
	.report-sheet { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

const CARD = "rounded-2xl border border-line bg-white p-5 shadow-card";

const countFormat = new Intl.NumberFormat("en-US");
const percentFormat = new Intl.NumberFormat("en-US", {
	maximumFractionDigits: 1,
});

const dzd = (value: number | null) => formatFigure(value, "dzd");
const count = (value: number | null) =>
	value === null ? "—" : countFormat.format(value);
const percent = (value: number | null) =>
	value === null || !Number.isFinite(value)
		? "—"
		: `${percentFormat.format(value)}%`;

interface WeeklyData {
	weekStart: string;
	current: WeekCounts;
	previous: WeekCounts;
	trainings: {
		trainingId: string;
		name: string;
		leads: number;
		contacted: number;
		confirmed: number;
		paid: number;
		revenue: number;
		collected: number;
	}[];
	editions: {
		editionId: number;
		editionNumber: number;
		training: { id: string; name: string };
		sessionsThisWeek: number;
		paid: number;
		expected: number | null;
	}[];
}

function WeeklyReportPage() {
	const { trpc } = useRouteContext({ from: "__root__" });
	const navigate = useNavigate({ from: Route.fullPath });
	const search = Route.useSearch();
	const thisWeek = weekStart(algiersToday());
	const start = weekStart(search.week ?? thisWeek);
	const isCurrent = start === thisWeek;

	const query = useQuery({
		...trpc.weeklyReport.get.queryOptions({ week: start }),
		placeholderData: keepPreviousData,
	});

	const title = `التقرير الأسبوعي — ${weekLabel(start)}`;
	useEffect(() => {
		const previous = document.title;
		document.title = title;
		return () => {
			document.title = previous;
		};
	}, [title]);

	function goTo(week: string) {
		void navigate({
			search: week === thisWeek ? {} : { week },
			replace: true,
		});
	}

	const data = query.data as WeeklyData | undefined;

	return (
		<div className="flex flex-col gap-5">
			<style>{PRINT_CSS}</style>

			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold text-navy">التقرير الأسبوعي</h1>
					<p className="mt-1 text-sm text-muted">
						أداء المبيعات B2C · <span dir="ltr">Performance B2C</span>
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-2 print:hidden">
					<GhostButton onClick={() => goTo(addDays(start, -7))}>
						<ChevronRight className="h-4 w-4" />
						السابق
					</GhostButton>
					{!isCurrent ? (
						<GhostButton onClick={() => goTo(thisWeek)}>
							هذا الأسبوع
						</GhostButton>
					) : null}
					<GhostButton
						onClick={() => goTo(addDays(start, 7))}
						disabled={start >= thisWeek}
					>
						التالي
						<ChevronLeft className="h-4 w-4" />
					</GhostButton>
					<GhostButton onClick={() => window.print()}>
						<Printer className="h-4 w-4" />
						طباعة / PDF
					</GhostButton>
				</div>
			</header>

			<div className="flex flex-wrap items-center gap-2">
				<p className="font-display text-lg font-bold text-navy">
					{weekLabel(start)}
				</p>
				{isCurrent ? (
					<span className="rounded-full bg-gold-50 px-2.5 py-0.5 text-xs font-bold text-gold-600">
						الأسبوع الجاري — الأرقام غير نهائية
					</span>
				) : null}
				{query.isFetching && data ? (
					<Loader2 className="h-4 w-4 animate-spin text-muted print:hidden" />
				) : null}
			</div>

			{query.isLoading ? (
				<div className="flex justify-center py-20">
					<Loader2 className="h-6 w-6 animate-spin text-muted" />
				</div>
			) : !data ? (
				<p className="py-16 text-center text-sm font-semibold text-red-600">
					{query.error?.message ?? "تعذّر تحميل التقرير"}
				</p>
			) : (
				<WeeklySheet data={data} />
			)}
		</div>
	);
}

function WeeklySheet({ data }: { data: WeeklyData }) {
	const { current: c, previous: p } = data;
	const rates = weekRates(c);
	const previousRates = weekRates(p);

	return (
		<div className="report-sheet flex flex-col gap-5">
			<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
				<Kpi
					label="الطلبات الجديدة"
					fr="Leads générés"
					value={count(c.leads)}
					change={<Change current={c.leads} previous={p.leads} />}
				/>
				<Kpi
					label="تم التواصل معهم"
					fr="Personnes contactées"
					value={count(c.contacted)}
					detail="طلبات غُيّرت حالتها أو أُرسلت لها رسالة"
					change={<Change current={c.contacted} previous={p.contacted} />}
				/>
				<Kpi
					label="الطبعات المبرمجة"
					fr="Formations programmées"
					value={count(c.scheduledEditions)}
					detail="طبعات لها يوم تكوين هذا الأسبوع"
					change={
						<Change
							current={c.scheduledEditions}
							previous={p.scheduledEditions}
						/>
					}
				/>
				<Kpi
					label="التسجيلات المدفوعة"
					fr="Inscriptions B2C payées"
					value={count(c.paid)}
					detail={`متوسط الامتلاء: ${percent(c.fillRate)}`}
					change={<Change current={c.paid} previous={p.paid} />}
				/>
				<Kpi
					label="رقم الأعمال المحقق"
					fr="Chiffre d'affaires généré"
					value={dzd(c.revenue)}
					detail={
						c.unpriced > 0
							? `${c.confirmed} تأكيد جديد — ${c.unpriced} منها بلا سعر بيع`
							: `${c.confirmed} تأكيد جديد`
					}
					warn={c.unpriced > 0}
					change={<Change current={c.revenue} previous={p.revenue} money />}
				/>
				<Kpi
					label="المبالغ المحصّلة"
					fr="Encaissement total"
					value={dzd(c.collected)}
					detail={`${c.payments} دفعة مسجّلة`}
					change={<Change current={c.collected} previous={p.collected} money />}
				/>
				<Kpi
					label="نسبة التحويل"
					fr="Contactés → inscrits payés"
					value={percent(rates.conversion)}
					detail={`${c.paid} مدفوع من ${c.contacted} تم التواصل معهم`}
					change={
						<Change
							current={rates.conversion}
							previous={previousRates.conversion}
							points
						/>
					}
				/>
				<Kpi
					label="نسبة التحصيل"
					fr="Taux de recouvrement"
					value={percent(rates.recovery)}
					detail={`الفارق بين رقم الأعمال والمحصّل: ${dzd(rates.gap)}`}
					warn={rates.gap > 0}
					change={
						<Change
							current={rates.recovery}
							previous={previousRates.recovery}
							points
						/>
					}
				/>
			</div>

			{c.unpriced > 0 || c.fillRate === null ? (
				<div className="rounded-2xl border border-gold-100 bg-gold-50 px-4 py-3 text-sm text-gold-600">
					<ul className="list-inside list-disc space-y-1">
						{c.unpriced > 0 ? (
							<li>
								{c.unpriced} تأكيد لم يُحتسب في رقم الأعمال لأن طبعته بلا سعر بيع
								— يُحدَّد السعر من تبويب «التكاليف والتسعير» للطبعة.
							</li>
						) : null}
						{c.fillRate === null && data.editions.length > 0 ? (
							<li>
								متوسط الامتلاء غير متاح: لا يوجد عدد حضور متوقع لطبعات هذا
								الأسبوع في تقارير التكاليف.
							</li>
						) : null}
					</ul>
				</div>
			) : null}

			<section className={CARD}>
				<h2 className="mb-3 font-display text-base font-bold text-navy">
					الطبعات المبرمجة هذا الأسبوع
				</h2>
				{data.editions.length === 0 ? (
					<p className="text-sm text-muted">لا توجد أيام تكوين هذا الأسبوع.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[34rem] border-collapse text-sm">
							<thead>
								<tr className="border-b border-line text-start text-xs text-muted">
									<th className="py-2 text-start font-bold">التدريب</th>
									<th className="py-2 text-start font-bold">الطبعة</th>
									<th className="py-2 text-start font-bold">
										أيام هذا الأسبوع
									</th>
									<th className="py-2 text-start font-bold">مدفوع / متوقع</th>
									<th className="py-2 text-start font-bold">الامتلاء</th>
								</tr>
							</thead>
							<tbody>
								{data.editions.map((edition) => {
									const fill =
										edition.expected && edition.expected > 0
											? (edition.paid / edition.expected) * 100
											: null;
									return (
										<tr
											key={edition.editionId}
											className="border-b border-line/60"
										>
											<td className="py-2.5 font-semibold text-ink">
												{edition.training.name}
											</td>
											<td className="py-2.5">
												<Link
													to="/admin/editions/$editionId"
													params={{ editionId: String(edition.editionId) }}
													search={{ tab: "costs" }}
													className="font-semibold text-teal-600 hover:underline"
												>
													{editionLabel(edition.editionNumber)}
												</Link>
											</td>
											<td className="py-2.5 tabular-nums">
												{edition.sessionsThisWeek || "—"}
											</td>
											<td className="py-2.5 tabular-nums">
												<bdi dir="ltr">
													{edition.paid} / {edition.expected ?? "—"}
												</bdi>
											</td>
											<td
												className={`py-2.5 font-bold tabular-nums ${
													fill === null
														? "text-muted"
														: fill >= 100
															? "text-emerald-700"
															: "text-navy"
												}`}
											>
												<bdi dir="ltr">{percent(fill)}</bdi>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section className={CARD}>
				<h2 className="mb-3 font-display text-base font-bold text-navy">
					حسب التدريب
				</h2>
				{data.trainings.length === 0 ? (
					<p className="text-sm text-muted">لا نشاط هذا الأسبوع.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[46rem] border-collapse text-sm">
							<thead>
								<tr className="border-b border-line text-xs text-muted">
									<th className="py-2 text-start font-bold">التدريب</th>
									<th className="py-2 text-start font-bold">طلبات جديدة</th>
									<th className="py-2 text-start font-bold">تواصل</th>
									<th className="py-2 text-start font-bold">تأكيدات</th>
									<th className="py-2 text-start font-bold">مدفوعة</th>
									<th className="py-2 text-start font-bold">رقم الأعمال</th>
									<th className="py-2 text-start font-bold">المحصّل</th>
								</tr>
							</thead>
							<tbody className="tabular-nums">
								{data.trainings.map((row) => (
									<tr key={row.trainingId} className="border-b border-line/60">
										<td className="py-2.5 font-semibold text-ink">
											{row.name}
										</td>
										<td className="py-2.5">{count(row.leads)}</td>
										<td className="py-2.5">{count(row.contacted)}</td>
										<td className="py-2.5">{count(row.confirmed)}</td>
										<td className="py-2.5">{count(row.paid)}</td>
										<td className="py-2.5 whitespace-nowrap">
											<bdi dir="ltr">{dzd(row.revenue)}</bdi>
										</td>
										<td className="py-2.5 whitespace-nowrap">
											<bdi dir="ltr">{dzd(row.collected)}</bdi>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<details className="rounded-2xl border border-line bg-white px-5 py-4 text-sm text-muted print:hidden">
				<summary className="cursor-pointer font-bold text-navy">
					كيف تُحسب الأرقام؟
				</summary>
				<ul className="mt-3 list-inside list-disc space-y-1.5 leading-relaxed">
					<li>الأسبوع من الأحد إلى السبت، بتوقيت الجزائر.</li>
					<li>الطلبات الجديدة: الطلبات المسجّلة خلال الأسبوع.</li>
					<li>
						تم التواصل معهم: الطلبات التي غيّر أحد الفريق حالتها أو أرسل لها
						رسالة واتساب خلال الأسبوع.
					</li>
					<li>
						التسجيلات المدفوعة: الطلبات التي انتقلت إلى C-02 خلال الأسبوع.
					</li>
					<li>
						الامتلاء: مدفوعو كل طبعة مبرمجة مقسومون على الحضور المتوقع في تقرير
						تكاليفها، ثم المتوسط.
					</li>
					<li>
						رقم الأعمال: الطلبات المؤكدة (C-01 أو C-02) لأول مرة خلال الأسبوع
						والتي ما زالت مؤكدة، بسعر بيع طبعتها.
					</li>
					<li>
						المحصّل: الدفعات المسجّلة على الطلبات بتاريخ استلام يقع في الأسبوع —
						قد تخص تأكيدات أسابيع سابقة، لذا قد تتجاوز نسبة التحصيل 100%.
					</li>
				</ul>
			</details>
		</div>
	);
}

function Kpi({
	label,
	fr,
	value,
	detail,
	warn = false,
	change,
}: {
	label: string;
	fr: string;
	value: string;
	detail?: string;
	warn?: boolean;
	change: React.ReactNode;
}) {
	return (
		<div
			className={`flex min-w-0 flex-col gap-1 rounded-2xl border p-4 shadow-card ${
				warn ? "border-gold-100 bg-gold-50" : "border-line bg-white"
			}`}
		>
			<p className="text-xs font-bold text-navy">{label}</p>
			<p className="text-[10px] font-semibold text-muted" dir="ltr">
				{fr}
			</p>
			<p className="truncate font-display text-xl font-bold tabular-nums text-navy">
				<bdi dir="ltr">{value}</bdi>
			</p>
			{change}
			{detail ? (
				<p className="text-[11px] leading-snug text-muted">{detail}</p>
			) : null}
		</div>
	);
}

/** Week-over-week change: green up, red down; rates move in points. */
function Change({
	current,
	previous,
	money = false,
	points = false,
}: {
	current: number | null;
	previous: number | null;
	money?: boolean;
	points?: boolean;
}) {
	const change = weekChange(current, previous);
	if (!change) {
		return <p className="text-[11px] text-muted">— مقارنة بالأسبوع السابق</p>;
	}
	if (change.diff === 0) {
		return <p className="text-[11px] text-muted">= مثل الأسبوع السابق</p>;
	}
	const up = change.diff > 0;
	const magnitude = points
		? `${percentFormat.format(Math.abs(change.diff))} نقطة`
		: money
			? dzd(Math.abs(change.diff))
			: countFormat.format(Math.abs(change.diff));
	return (
		<p
			className={`flex flex-wrap items-center gap-1 text-[11px] font-bold ${
				up ? "text-emerald-700" : "text-red-600"
			}`}
		>
			{up ? (
				<TrendingUp className="h-3.5 w-3.5" />
			) : (
				<TrendingDown className="h-3.5 w-3.5" />
			)}
			<bdi dir="ltr">
				{up ? "+" : "−"}
				{magnitude}
				{!points && change.percent !== null
					? ` (${up ? "+" : "−"}${percentFormat.format(Math.abs(change.percent))}%)`
					: ""}
			</bdi>
			<span className="font-normal text-muted">عن الأسبوع السابق</span>
		</p>
	);
}
